"use client";

/**
 * InteractionErrorToast — shows when resolving an interaction (POST /resolve)
 * fails after retries.
 *
 * Previously a failed resolve silently cleared the pending picker, eating the
 * user's answer with zero feedback. Now the picker is restored (so they can
 * retry) and this transient error explains what happened. Auto-dismisses after
 * 5 seconds.
 */

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useDockVisible } from "@/lib/home-flags-hooks";
import { useTaskStore } from "@/stores/taskStore";
import { AlertCircleIcon } from "@/components/icons/AlertCircleIcon";
import { CloseIcon } from "@/components/icons/CloseIcon";

export function InteractionErrorToast() {
  const dockVisible = useDockVisible(usePathname());
  const interactionError = useTaskStore((s) => s.interactionError);
  const dismissInteractionError = useTaskStore(
    (s) => s.dismissInteractionError
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (interactionError) {
      timerRef.current = setTimeout(() => {
        dismissInteractionError();
      }, 5000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [interactionError, dismissInteractionError]);

  if (!interactionError) return null;

  return (
    <div
      className={cn(
        "fixed left-1/2 -translate-x-1/2 z-[80] flex items-center gap-3 px-4 py-3 rounded-16 text-3.5 shadow-lg bg-palette-red/15 border border-solid border-palette-red/30 text-palette-red backdrop-blur-12 animate-style-interaction-error-toast-inline",
        dockVisible ? "bottom-28" : "bottom-6"
      )}
      role="alert"
    >
      <AlertCircleIcon />
      <span>{interactionError}</span>
      <button
        onClick={dismissInteractionError}
        className="ml-2 p-1 rounded hover:bg-white/10 transition-colors flex-shrink-0"
        aria-label="Dismiss error"
      >
        <CloseIcon width={14} height={14} />
      </button>
    </div>
  );
}
